import useCurrentUser from "@/store/useCurrentUser";
import { Search } from "lucide-react";
import { useLocation } from "react-router-dom";
import { paths } from "@/config/paths";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLogout } from "@/features/auth/api/auth-api";
import {
  useUnreadCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from "@/features/notifications/api/notifications-api";
import NotificationDropdown from "@/features/notifications/components/notification-dropdown";

// ─── Derive page title from current route ────────────────────────────────────────
const routeTitleMap: Record<string, string> = {
  [paths.app.dashboard.getHref()]: "Dashboard",
  [paths.app.projects.getHref()]: "Projects",
  [paths.app.invoices.getHref()]: "Invoices",
  [paths.app.contractors.getHref()]: "Contractors",
  [paths.app.dailyLogs.getHref()]: "Daily logs",
  [paths.app.documents.getHref()]: "Documents",
  [paths.app.users.getHref()]: "Users",
  [paths.app.purchaseOrders.getHref()]: "Purchase orders",
  [paths.app.suppliers.getHref()]: "Suppliers",
  [paths.app.changeOrders.getHref()]: "Change orders",
  [paths.app.milestones.getHref()]: "Milestones",
  [paths.app.punchlists.getHref()]: "Punch lists",
  [paths.app.photos.getHref()]: "Photos",
  [paths.app.materials.getHref()]: "Materials",
  [paths.app.clients.getHref()]: "Clients",
  [paths.app.notifications.getHref()]: "Notifications",
  [paths.app.auditLogs.getHref()]: "Audit logs",
  [paths.app.reports.getHref()]: "Reports",
  [paths.app.reports.financial.getHref()]: "Financial Report",
  [paths.app.reports.projects.getHref()]: "Project Report",
  [paths.app.reports.timeTracking.getHref()]: "Time Tracking Report",
  [paths.app.settings.getHref()]: "Settings",
  [paths.app.settings.profile.getHref()]: "Profile",
  [paths.app.settings.preferences.getHref()]: "Preferences",
  [paths.app.settings.company.getHref()]: "Company Settings",
  [paths.app.settings.notifications.getHref()]: "Notification Settings",
  [paths.app.portal.getHref()]: "Client Portal",
};

function getPageTitle(pathname: string): string {
  // Exact match first
  if (routeTitleMap[pathname]) return routeTitleMap[pathname];
  // Fallback: check if any key is a prefix of the path
  for (const [route, title] of Object.entries(routeTitleMap)) {
    if (pathname.startsWith(route + "/")) return title;
  }
  // Last resort: humanize the last segment
  const last = pathname.split("/").filter(Boolean).pop() ?? "Dashboard";
  return last.charAt(0).toUpperCase() + last.slice(1).replace(/-/g, " ");
}

// ─── TopNav component ────────────────────────────────────────────────────────────
const TopNav = () => {
  const user = useCurrentUser((state: any) => state.user);
  const location = useLocation();
  const pageTitle = getPageTitle(location.pathname);

  const { data: unreadCount = 0 } = useUnreadCount();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const userInitials = (user?.fullName ?? "")
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <header className="h-14 bg-(--color-bg-surface) border-b border-(--color-border-default) flex items-center justify-between px-4 md:px-6 shrink-0 z-10 gap-4">
      {/* ── Page title ────────────────────────────────────────── */}
      <h1 className="text-lg font-medium text-(--color-text-primary) whitespace-nowrap shrink-0">
        {pageTitle}
      </h1>

      {/* ── Right side actions ────────────────────────────────── */}
      <div className="flex items-center gap-2 ml-auto">
        {/* Search — desktop only */}
        <div className="hidden md:flex items-center gap-2 bg-(--color-bg-muted) rounded-lg px-3 py-1.5 w-56">
          <Search className="w-4 h-4 text-(--color-text-tertiary) shrink-0" />
          <input
            type="text"
            placeholder="Search projects..."
            className="bg-transparent border-none outline-none text-sm text-(--color-text-primary) placeholder:text-(--color-text-tertiary) w-full"
          />
        </div>

        {/* Notification bell */}
        <NotificationDropdown
          unreadCount={unreadCount}
          onMarkRead={(id) => markRead.mutate(id)}
          onMarkAllRead={() => markAllRead.mutate(undefined)}
          viewAllHref={paths.app.notifications.getHref()}
        />

        <UserProfileDropdownMenu>
          <div className="w-8 h-8 rounded-full bg-(--color-info-500) flex items-center justify-center text-white text-xs font-medium">
            {userInitials || "?"}
          </div>
        </UserProfileDropdownMenu>
      </div>
    </header>
  );
};

function UserProfileDropdownMenu({ children }: { children: React.ReactNode }) {
  const logout = useLogout();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="shrink-0 flex items-center justify-center focus:outline-none focus-visible:outline-none"
          aria-label="Profile"
        >
          {children}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem>Profile</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="destructive" onClick={() => logout()}>
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default TopNav;
